/**
 * Frontend Sentry initialisation. No-op when `VITE_SENTRY_DSN` is unset
 * so dev / preview builds without monitoring stay quiet.
 *
 * The same scrub philosophy as the backend (see
 * `@workspace/api-server/src/lib/sentryScrub.ts`) is applied: passwords,
 * tokens, session cookies, and student-PII-shaped fields are stripped
 * before any event leaves the browser.
 */
import * as Sentry from "@sentry/react";

const SENSITIVE_KEY_PATTERNS = [
  /password/i,
  /passwd/i,
  /secret/i,
  /token/i,
  /api[_-]?key/i,
  /session/i,
  /cookie/i,
  /authorization/i,
];

// Specific name fields are PII. We deliberately do NOT include the
// generic "name" key here because Sentry's own contexts use it for
// non-PII telemetry (browser.name, os.name, runtime.name, device.name)
// which we want to preserve for triage.
const PII_KEY_NAMES = new Set([
  "email",
  "emailAddress",
  "email_address",
  "phone",
  "phoneNumber",
  "phone_number",
  "fullName",
  "full_name",
  "displayName",
  "display_name",
  "givenName",
  "given_name",
  "firstName",
  "first_name",
  "lastName",
  "last_name",
  "surname",
  "preferredName",
  "preferred_name",
  "studentName",
  "studentNames",
  "student_name",
  "student_names",
  "studentId",
  "studentIds",
  "student_id",
  "student_ids",
  "schoolCode",
  "school_code",
]);

function isSensitiveKey(key: string): boolean {
  if (PII_KEY_NAMES.has(key)) return true;
  return SENSITIVE_KEY_PATTERNS.some((re) => re.test(key));
}

function scrub<T>(value: T, depth = 0): T {
  if (depth > 8 || value == null) return value;
  if (Array.isArray(value)) {
    return value.map((v) => scrub(v, depth + 1)) as unknown as T;
  }
  if (typeof value !== "object") return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKey(k) ? "[Filtered]" : scrub(v, depth + 1);
  }
  return out as T;
}

export function initFrontendSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ??
      (import.meta.env.PROD ? "production" : "development"),
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1"),
    sendDefaultPii: false,
    beforeSend(event) {
      try {
        if (event.request) {
          if (event.request.headers) {
            const headers: Record<string, string> = {};
            for (const [k, v] of Object.entries(event.request.headers)) {
              const lc = k.toLowerCase();
              headers[k] =
                lc === "authorization" || lc === "cookie" || lc === "set-cookie"
                  ? "[Filtered]"
                  : (v as string);
            }
            event.request.headers = headers;
          }
          if (event.request.cookies !== undefined) {
            event.request.cookies = "[Filtered]" as never;
          }
          if (event.request.data !== undefined) {
            event.request.data = scrub(event.request.data);
          }
        }
        if (event.extra) event.extra = scrub(event.extra);
        if (event.contexts) event.contexts = scrub(event.contexts);
        if (event.user) {
          event.user = event.user.id != null
            ? { id: String(event.user.id) }
            : undefined;
        }
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((bc) => ({
            ...bc,
            data: bc.data ? scrub(bc.data) : bc.data,
          }));
        }
        return event;
      } catch {
        return null;
      }
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        try {
          breadcrumb.data = scrub(breadcrumb.data);
        } catch {
          return null;
        }
      }
      return breadcrumb;
    },
  });
}

export function setSentryUser(userId: string | null | undefined): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
    Sentry.setTag("school_code", undefined);
  }
}

/**
 * Tags the active Sentry scope with the user's school_code so that
 * frontend errors can be filtered/grouped by school. Called from
 * `AuthGuard` once the profile has loaded; safely no-ops when Sentry
 * is disabled or no school is associated with the account.
 */
export function setSentrySchoolCode(
  schoolCode: string | null | undefined,
): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setTag("school_code", schoolCode ?? undefined);
}

export { Sentry };
